import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { StorageService } from './storage.service';
import { AccountStatus } from '../models/certificate.model';
import { StudentEmailModalComponent } from '../components/student-email-modal/student-email-modal.component';

/**
 * Service gérant l'affichage de la modal bloquante d'email étudiant
 *
 * Responsabilités:
 * - Vérifier si la modal doit être affichée au démarrage
 * - Gérer l'état de la modal (affichée/fermée)
 * - Empêcher l'affichage multiple
 */
@Injectable({
  providedIn: 'root'
})
export class StudentEmailModalService {
  private modalInstance: HTMLIonModalElement | null = null;
  private isModalOpen = false;

  constructor(
    private modalController: ModalController,
    private storageService: StorageService
  ) {}

  /**
   * Vérifie si l'utilisateur doit renseigner son email étudiant
   *
   * Conditions de déclenchement:
   * 1. Migration: Utilisateur avec certi_scolarite MAIS sans email_etudiant
   * 2. Renouvellement: statutcomptId === RENOUVELLEMENT OU null
   */
  shouldShowModal(): boolean {
    const user = this.storageService.getUser();

    if (!user) {
      return false;
    }

    // Certificat en attente de validation → pas de modal
    if (Number(user.statutcomptId) === AccountStatus.ATTENTE) {
      return false;
    }

    // A déjà un email étudiant validé → pas de modal
    if (user.email_etudiant) {
      return false;
    }

    // A déjà soumis un certificat (même sans email) → pas de modal
    if (user.certi_scolarite) {
      return false;
    }

    // Statut RENOUVELLEMENT sans email → géré par CertificatRejeteComponent (écran bloquant)
    if (user.statutcomptId === AccountStatus.RENOUVELLEMENT) {
      return false;
    }

    // Ni email ni certificat et statut non défini (anciens comptes en migration)
    return user.statutcomptId == null;
  }

  /**
   * Détermine la raison d'affichage de la modal
   */
  private getModalReason(): 'migration' | 'renouvellement' {
    const user = this.storageService.getUser();

    if (user && user.certi_scolarite && !user.email_etudiant) {
      return 'migration';
    }

    return 'renouvellement';
  }

  /**
   * Affiche la modal bloquante si nécessaire
   *
   * @returns Promise<boolean> - true si la modal a été affichée, false sinon
   */
  async showModalIfNeeded(): Promise<boolean> {
    // Ne pas afficher si déjà ouverte
    if (this.isModalOpen || this.modalInstance) {
      console.log('[StudentEmailModal] Modal déjà ouverte');
      return false;
    }

    // Vérifier si la modal doit être affichée
    if (!this.shouldShowModal()) {
      console.log('[StudentEmailModal] Conditions non remplies');
      return false;
    }

    console.log('[StudentEmailModal] Affichage de la modal bloquante');
    await this.presentModal();
    return true;
  }

  /**
   * Crée et affiche la modal
   */
  private async presentModal(): Promise<void> {
    this.isModalOpen = true;
    const reason = this.getModalReason();

    this.modalInstance = await this.modalController.create({
      component: StudentEmailModalComponent,
      componentProps: {
        reason: reason
      },
      backdropDismiss: false, // CRITIQUE: Modal non-fermable
      keyboardClose: false,    // CRITIQUE: Empêche fermeture au clavier
      cssClass: 'student-email-modal'
    });

    // Écouter la fermeture de la modal
    this.modalInstance.onDidDismiss().then((result) => {
      console.log('[StudentEmailModal] Modal fermée', result);
      this.isModalOpen = false;
      this.modalInstance = null;

      // Si succès, recharger les données utilisateur est déjà géré dans le composant
      if (result.data?.success) {
        console.log('[StudentEmailModal] Email étudiant validé avec succès');
      }
    });

    await this.modalInstance.present();
  }

  /**
   * Ferme la modal (utilisé uniquement en cas de succès depuis le composant)
   */
  async dismissModal(): Promise<void> {
    if (this.modalInstance) {
      await this.modalInstance.dismiss();
      this.isModalOpen = false;
      this.modalInstance = null;
    }
  }

  /**
   * Vérifie si la modal est actuellement ouverte
   */
  isOpen(): boolean {
    return this.isModalOpen;
  }
}
